import { Theme, createTheme } from '@mui/material/styles'
import { ActivityBlockType } from './activityBlock'

// Light surfaces use these; anything painted on `deep` takes `onDark` (ADR-054).
export const advancedColors = {
  canvas: '#F8F8F8',
  surface: '#FFFFFF',
  cardBg: '#F8F8F8',
  surfaceSunken: '#EBF1E9',
  grayLightLabel: '#F0F0F0',

  ink: '#0D5435',
  inkMuted: '#4D545C',
  inkFaint: '#686D74',
  line: '#C0C0C0',
  lineStrong: '#C0C0C0',

  deep: '#0D5435',
  deepStrong: '#0B492E',
  // `deep` + 6% / 18% white: a card on the drawer and its hairline.
  deepRaised: '#1C5E41',
  deepLine: '#397359',
  green: '#0E7B43',
  greenHover: '#189946',
  lightGreen: '#93D796',
  greenSoft: '#EBF1E9',
  greenSubtle: '#125B38',

  accent: '#0E7B43',
  accentSoft: '#EBF1E9',

  amber: '#8C6521',
  amberSoft: '#FFF7E5',
  yellowAccent: '#F2D365',
  chartBlue: '#5680CD',
  progressFill: '#B0CAFE',
  error: '#9F0C21',
  errorLight: '#FEBCC6',
} as const

export const surfaces = {
  violet: { bg: '#F3F0F8', fg: '#5E4A7A', stroke: '#C9BFD4' },
  green: { bg: '#EBF1E9', fg: '#0D5435', stroke: '#A8C2B3' },
  blue: { bg: '#E1EAFC', fg: '#345695', stroke: '#C2CCDF' },
  earth: { bg: '#F4F1EA', fg: '#8C6521', stroke: '#D5C7AE' },
  pink: { bg: '#FAF2F3', fg: '#9B5F66', stroke: '#DDC6C9' },
  yellow: { bg: '#FFF7E5', fg: '#8C6521', stroke: '#D5C7AE' },
} as const

// Contrast-checked against `deep`; text takes a token, not alpha (ADR-029).
export const onDark = {
  primary: advancedColors.surface,
  secondary: advancedColors.grayLightLabel,
  track: advancedColors.greenSubtle,
  stroke: advancedColors.deepStrong,
  // Every accent below clears 4.5:1 on `surface`; a lighter fill would drop `accentGreen` under it.
  surface: advancedColors.deepRaised,
  line: advancedColors.deepLine,
  accentGreen: advancedColors.lightGreen,
  accentBlue: advancedColors.progressFill,
  yellowAccent: advancedColors.yellowAccent,
  // errorLight on deep is ~5.7:1 — clears the 4.5:1 AA minimum for text of any size, not just large.
  accentRed: advancedColors.errorLight,
} as const

export const caseloadInk = {
  default: {
    primary: onDark.primary,
    muted: onDark.secondary,
    green: onDark.accentGreen,
    blue: onDark.accentBlue,
    red: onDark.accentRed,
    chipFg: onDark.secondary,
  },
  selected: {
    primary: surfaces.green.fg,
    muted: advancedColors.inkMuted,
    green: advancedColors.green,
    blue: surfaces.blue.fg,
    red: advancedColors.error,
    chipFg: surfaces.green.fg,
  },
} as const

export type CaseloadInk = (typeof caseloadInk)[keyof typeof caseloadInk]

// Planner bars: each pair clears 4.5:1 for caption text, and neighbouring hues differ.
export const plannerTrackColors = [
  { bg: '#7BC96F', fg: '#0E3D12' },
  { bg: '#4EB8C9', fg: '#083844' },
  { bg: '#6A93E0', fg: '#0E2248' },
  { bg: '#B08AE8', fg: '#2E1658' },
  { bg: '#F3A6C0', fg: '#7A2044' },
  { bg: '#F0C14A', fg: '#6B4A0A' },
  { bg: '#F4B183', fg: '#6A2808' },
] as const

// A bar's fill by how live its track is, and the fade on its content once it isn't running.
export const plannerBar = {
  fillAlpha: { running: 1, pending: 0.4, faded: 0.2 },
  fadedContentOpacity: 0.85,
  draggingOpacity: 0.6,
} as const

export const TRACK_STATE_COLORS = {
  intake: advancedColors.inkFaint,
  scheduled: advancedColors.inkFaint,
  completed: advancedColors.green,
  active: advancedColors.deep,
  stalled: advancedColors.line,
  closed: advancedColors.inkFaint,
} as const

export const ACTIVITY_TYPE_COLORS: Record<ActivityBlockType, string> = {
  session: advancedColors.green,
  checkIn: advancedColors.chartBlue,
  reflection: surfaces.pink.fg,
  survey: advancedColors.deep,
  lesson: surfaces.earth.fg,
  restDay: advancedColors.inkMuted,
}

// The main column and info drawer are separate subtrees, so cache per base theme.
const cache = new WeakMap<Theme, Theme>()

export const createAdvancedTheme = (base: Theme): Theme => {
  const cached = cache.get(base)
  if (cached) {
    return cached
  }

  const theme = createTheme(base, {
    typography: {
      overline: {
        fontSize: '0.875rem',
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: '0.02em',
        textTransform: 'none',
        color: advancedColors.inkMuted,
      },
    },
    palette: {
      primary: {
        main: advancedColors.green,
        dark: advancedColors.deep,
        light: advancedColors.lightGreen,
        contrastText: advancedColors.surface,
      },
      error: { main: advancedColors.error },
      background: { default: advancedColors.canvas, paper: advancedColors.surface },
      text: { primary: advancedColors.ink, secondary: advancedColors.inkMuted },
      divider: advancedColors.line,
      darkestGreen: advancedColors.deep,
      lightestGrey: advancedColors.greenSoft,
    },
    shape: { borderRadius: 12 },
    components: {
      MuiAlert: {
        styleOverrides: {
          root: { alignItems: 'center' },
          action: { paddingTop: 0, alignSelf: 'center' },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${advancedColors.line}`,
            boxShadow: '0 1px 2px rgba(11, 73, 46, 0.16)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { boxShadow: base.shadows[24] },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            ...base.typography.h3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: base.spacing(1),
            padding: base.spacing(1.5, 2.5),
            minHeight: 56,
            boxSizing: 'border-box',
            color: advancedColors.ink,
            borderBottom: `1px solid ${advancedColors.line}`,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: base.spacing(2.5),
            '.MuiDialogTitle-root + &': { paddingTop: base.spacing(2.5) },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: base.spacing(2, 2.5),
            borderTop: `1px solid ${advancedColors.line}`,
          },
        },
      },
      // AppBar is a Paper; the card hairline and radius would otherwise frame the chrome in gray.
      MuiAppBar: {
        styleOverrides: {
          root: {
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            backgroundImage: 'none',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 999,
            paddingInline: base.spacing(2.5),
          },
          containedPrimary: {
            '&:hover': { backgroundColor: advancedColors.greenHover },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            letterSpacing: '0.01em',
            borderRadius: 8,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 6,
            borderRadius: 999,
            backgroundColor: advancedColors.cardBg,
          },
          bar: { borderRadius: 999, backgroundColor: advancedColors.progressFill },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          // `deep` is the info-drawer / caseload chrome — a tooltip painted the same colour vanishes.
          tooltip: {
            backgroundColor: advancedColors.inkMuted,
            color: onDark.primary,
            fontSize: '0.75rem',
            padding: '6px 10px',
          },
          arrow: { color: advancedColors.inkMuted },
          // Standard's `CustomTooltip` paints its tooltip black through a class that outranks the slots above.
          popper: {
            '& .MuiTooltip-tooltip': { backgroundColor: advancedColors.inkMuted, color: onDark.primary },
            '& .MuiTooltip-arrow': { color: advancedColors.inkMuted },
          },
        },
      },
      // Clips overflow; don't replace with an opaque background — dialogs sit on `surface`.
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            overflow: 'hidden',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: advancedColors.deep },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: advancedColors.deep },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: advancedColors.deep },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: { backgroundColor: 'transparent' },
        },
      },
      // Hints start flush with the field's edge, not MUI's 14px input inset.
      MuiFormHelperText: {
        styleOverrides: {
          contained: { marginLeft: 0, marginRight: 0 },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          // MUI gives the textarea `background: none`, so `root`'s colour must be repeated on `input`.
          root: { backgroundColor: advancedColors.surface },
          input: { backgroundColor: advancedColors.surface },
        },
      },
    },
  })

  cache.set(base, theme)

  return theme
}
